/**
 * Match device labels scanned in Receiving to an open RMA.
 * Arrivals often do not know a carton is a return, so Receiving discovers it here.
 */

import type { PoolClient } from "pg";

import {
  sumRmaOrderedDeviceCount,
} from "../quality/quality-rma-helpers.js";

export const INBOUND_RECEIVE_RMA_QC_ANALYSIS_LABEL = "QC Analysis";

const RMA_DEVICE_CHECK_IN_STATUSES = ["awaiting_return", "received"] as const;

export function inboundReceiveRmaCountCheckedMessage(rmaNumber: string): string {
  return `We have received ${rmaNumber}. The count of devices checks out. Take this carton to the QC Analysis station.`;
}

export function inboundReceiveRmaFinishWithDiscrepancyMessage(rmaNumber: string): string {
  return `We have received ${rmaNumber}. Take this carton to the QC Analysis station. Quality was notified of a quantity discrepancy on this RMA.`;
}

export type InboundReceiveRmaFinishEvaluation = {
  rmaId: string;
  rmaNumber: string;
  orderedDeviceCount: number;
  checkedInCount: number;
  listedDeviceCount: number;
  quantityDiscrepancy: boolean;
  countChecksOut: boolean;
  canFinish: boolean;
};

export function evaluateRmaReceiveQuantityDiscrepancy(input: {
  quantityAndModels: string | null;
  checkedInSerials: readonly string[];
}): {
  orderedDeviceCount: number;
  checkedInCount: number;
  quantityDiscrepancy: boolean;
} {
  const checkedInCount = normalizeInboundReceiveDeviceSerialList(input.checkedInSerials).length;
  const orderedDeviceCount = sumRmaOrderedDeviceCount(input.quantityAndModels);
  let quantityDiscrepancy = false;
  if (orderedDeviceCount > 0) {
    quantityDiscrepancy = checkedInCount !== orderedDeviceCount;
  } else if (checkedInCount > 0) {
    quantityDiscrepancy = true;
  }
  return { orderedDeviceCount, checkedInCount, quantityDiscrepancy };
}

export async function evaluateInboundReceiveRmaFinish(
  client: PoolClient,
  input: { rmaNumber: string; scannedSerials: readonly string[] },
): Promise<InboundReceiveRmaFinishEvaluation | null> {
  const rma = await client.query<{
    id: string;
    rma_number: string;
    quantity_and_models: string | null;
  }>(
    `select quality.rma.id::text,
            quality.rma.rma_number,
            quality.rma.quantity_and_models
       from quality.rma
      where upper(trim(quality.rma.rma_number)) = $1
        and quality.rma.status::text = any($2::text[])
      limit 1`,
    [normalizeInboundReceiveDeviceSerial(input.rmaNumber), RMA_DEVICE_CHECK_IN_STATUSES],
  );
  const row = rma.rows[0];
  if (row == null) {
    return null;
  }
  const listed = await client.query<{ device_label_serial: string }>(
    `select quality.rma_device.device_label_serial
       from quality.rma_device
      where quality.rma_device.rma_id = $1::bigint
      order by quality.rma_device.id`,
    [row.id],
  );
  const listedSerials = listed.rows.map((device) => device.device_label_serial);
  const deviceCount = evaluateInboundReceiveRmaDeviceCount({
    rmaId: row.id,
    rmaNumber: row.rma_number,
    expectedSerials: listedSerials,
    scannedSerials: input.scannedSerials,
  });
  const quantity = evaluateRmaReceiveQuantityDiscrepancy({
    quantityAndModels: row.quantity_and_models,
    checkedInSerials: input.scannedSerials,
  });
  const checkedInCount = quantity.checkedInCount;
  return {
    rmaId: row.id,
    rmaNumber: row.rma_number,
    orderedDeviceCount: quantity.orderedDeviceCount,
    checkedInCount,
    listedDeviceCount: listedSerials.length,
    quantityDiscrepancy: quantity.quantityDiscrepancy,
    countChecksOut: deviceCount.countChecksOut && !quantity.quantityDiscrepancy,
    canFinish: checkedInCount > 0,
  };
}

export async function completeInboundReceiveRmaRouting(
  client: PoolClient,
  input: {
    rmaNumber: string;
    deviceSerials: readonly string[];
  },
): Promise<InboundReceiveRmaFinishEvaluation> {
  const finish = await evaluateInboundReceiveRmaFinish(client, {
    rmaNumber: input.rmaNumber,
    scannedSerials: input.deviceSerials,
  });
  if (finish == null) {
    throw new InboundReceiveRmaDeviceCheckInError("That RMA is not open for receiving.", 404);
  }
  if (!finish.canFinish) {
    throw new InboundReceiveRmaDeviceCheckInError("Scan at least one device before finishing this return.", 400);
  }
  const rmaId = Number.parseInt(finish.rmaId, 10);
  if (!Number.isSafeInteger(rmaId) || rmaId <= 0) {
    throw new InboundReceiveRmaDeviceCheckInError("That RMA is not open for receiving.", 404);
  }
  await markRmaReceivedAfterDeviceCount(client, rmaId);
  await client.query(
    `update quality.rma
        set receive_quantity_discrepancy = $2,
            updated_at = now()
      where quality.rma.id = $1`,
    [rmaId, finish.quantityDiscrepancy],
  );
  return finish;
}

export function normalizeInboundReceiveDeviceSerial(value: string): string {
  return value.trim().replace(/\s+/g, " ").toUpperCase();
}

export function normalizeInboundReceiveDeviceSerialList(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const serials: string[] = [];
  for (const value of values) {
    const serial = normalizeInboundReceiveDeviceSerial(value);
    if (serial === "" || seen.has(serial)) continue;
    seen.add(serial);
    serials.push(serial);
  }
  return serials;
}

export type InboundReceiveRmaDeviceCount = {
  rmaId: string;
  rmaNumber: string;
  expectedCount: number;
  matchedSerials: string[];
  missingSerials: string[];
  countChecksOut: boolean;
};

export function evaluateInboundReceiveRmaDeviceCount(input: {
  rmaId: string;
  rmaNumber: string;
  expectedSerials: readonly string[];
  scannedSerials: readonly string[];
}): InboundReceiveRmaDeviceCount {
  const expected = normalizeInboundReceiveDeviceSerialList(input.expectedSerials);
  const scanned = new Set(normalizeInboundReceiveDeviceSerialList(input.scannedSerials));
  const matchedSerials = expected.filter((serial) => scanned.has(serial));
  const missingSerials = expected.filter((serial) => !scanned.has(serial));
  return {
    rmaId: input.rmaId,
    rmaNumber: input.rmaNumber,
    expectedCount: expected.length,
    matchedSerials,
    missingSerials,
    countChecksOut: expected.length > 0 && missingSerials.length === 0,
  };
}

type RmaDeviceRow = {
  rma_id: string;
  rma_number: string;
  device_label_serial: string;
};

export async function matchAwaitingRmaDevices(
  client: PoolClient,
  scannedSerials: readonly string[],
): Promise<{
  matches: InboundReceiveRmaDeviceCount[];
  unmatchedSerials: string[];
  confirmed: InboundReceiveRmaDeviceCount | null;
}> {
  const scanned = normalizeInboundReceiveDeviceSerialList(scannedSerials);
  if (scanned.length === 0) {
    return { matches: [], unmatchedSerials: [], confirmed: null };
  }
  const hit = await client.query<{ rma_id: string }>(
    `select distinct quality.rma.id::text as rma_id
       from quality.rma
       join quality.rma_device
         on quality.rma_device.rma_id = quality.rma.id
      where quality.rma.status in ('awaiting_return', 'received')
        and upper(trim(quality.rma_device.device_label_serial)) = any($1::text[])`,
    [scanned],
  );
  const rmaIds = hit.rows.map((row) => row.rma_id);
  if (rmaIds.length === 0) {
    return { matches: [], unmatchedSerials: scanned, confirmed: null };
  }
  const devices = await client.query<RmaDeviceRow>(
    `select quality.rma.id::text as rma_id,
            quality.rma.rma_number,
            quality.rma_device.device_label_serial
       from quality.rma
       join quality.rma_device
         on quality.rma_device.rma_id = quality.rma.id
      where quality.rma.id = any($1::bigint[])
      order by quality.rma.rma_number, quality.rma_device.id`,
    [rmaIds],
  );
  const byRma = new Map<string, { rmaNumber: string; serials: string[] }>();
  for (const row of devices.rows) {
    const current = byRma.get(row.rma_id) ?? { rmaNumber: row.rma_number, serials: [] };
    current.serials.push(row.device_label_serial);
    byRma.set(row.rma_id, current);
  }
  const matches = [...byRma.entries()].map(([rmaId, row]) =>
    evaluateInboundReceiveRmaDeviceCount({
      rmaId,
      rmaNumber: row.rmaNumber,
      expectedSerials: row.serials,
      scannedSerials: scanned,
    }),
  );
  const matched = new Set(matches.flatMap((row) => row.matchedSerials));
  const unmatchedSerials = scanned.filter((serial) => !matched.has(serial));
  const result = { matches, unmatchedSerials };
  return { ...result, confirmed: confirmedInboundReceiveRmaMatch(result) };
}

export async function matchInboundReceiveRmaDevicesForApi(
  client: PoolClient,
  scannedSerials: readonly string[],
  rmaNumber?: string | null,
): Promise<{
  matches: InboundReceiveRmaDeviceCount[];
  unmatchedSerials: string[];
  confirmed: InboundReceiveRmaDeviceCount | null;
  finish: InboundReceiveRmaFinishEvaluation | null;
}> {
  const base = await matchAwaitingRmaDevices(client, scannedSerials);
  const trimmed = (rmaNumber ?? "").trim();
  const finish =
    trimmed !== ""
      ? await evaluateInboundReceiveRmaFinish(client, { rmaNumber: trimmed, scannedSerials })
      : null;
  return { ...base, finish };
}

export function confirmedInboundReceiveRmaMatch(input: {
  matches: readonly InboundReceiveRmaDeviceCount[];
  unmatchedSerials: readonly string[];
}): InboundReceiveRmaDeviceCount | null {
  if (input.unmatchedSerials.length > 0) return null;
  const hit = input.matches.filter((row) => row.matchedSerials.length > 0);
  if (hit.length !== 1 || !hit[0].countChecksOut) return null;
  return hit[0];
}

export type InboundReceiveRmaDeviceCheckIn = {
  id: string;
  deviceSerial: string;
  sku: string | null;
  endUserName: string | null;
  checkedInAt: string;
};

export type InboundReceiveRmaDeviceCheckInList = {
  rmaId: string;
  rmaNumber: string;
  deviceSerials: string[];
  devices: InboundReceiveRmaDeviceCheckIn[];
};

function mapRmaDeviceCheckInList(
  rmaId: string,
  rmaNumber: string,
  rows: Array<{
    id: string;
    device_label_serial: string;
    finished_sku: string | null;
    end_user_name: string | null;
    checked_in_at: string;
  }>,
): InboundReceiveRmaDeviceCheckInList {
  const devices = rows.map((device) => ({
    id: device.id,
    deviceSerial: device.device_label_serial,
    sku: device.finished_sku?.trim() || null,
    endUserName: device.end_user_name?.trim() || null,
    checkedInAt: device.checked_in_at,
  }));
  return {
    rmaId,
    rmaNumber,
    deviceSerials: devices.map((device) => device.deviceSerial),
    devices,
  };
}

async function loadRmaDeviceCheckInRows(
  client: PoolClient,
  rmaId: string,
): Promise<InboundReceiveRmaDeviceCheckInList["devices"]> {
  const devices = await client.query<{
    id: string;
    device_label_serial: string;
    finished_sku: string | null;
    end_user_name: string | null;
    checked_in_at: string;
  }>(
    `select quality.rma_device_check_in.id::text,
            quality.rma_device_check_in.device_label_serial,
            quality.rma_device_check_in.checked_in_at::text,
            coalesce(
              nullif(trim(client.data.fs_account_name), '')
            ,
              nullif(trim(client.data.mv_end_user_name), '')) as end_user_name,
            coalesce(
              (
                select public.serial_fci.finished_sku
                  from public.serial_fci
                 where quality.rma_device.serial_fci_id is not null
                   and public.serial_fci.id = quality.rma_device.serial_fci_id
                 limit 1
              ),
              (
                select public.serial_fci.finished_sku
                  from public.serial_fci
                 where public.serial_fci.archived_at is null
                   and upper(trim(public.serial_fci.device_label_serial)) = quality.rma_device_check_in.device_label_serial
                 order by public.serial_fci.id desc
                 limit 1
              )
            ) as finished_sku
       from quality.rma_device_check_in
       join quality.rma
         on quality.rma.id = quality.rma_device_check_in.rma_id
       left join quality.rma_device
         on quality.rma_device.id = quality.rma_device_check_in.rma_device_id
       left join client.data
         on client.data.fs_account_id = quality.rma.end_user_fs_account_id
      where quality.rma_device_check_in.rma_id = $1::bigint
      order by quality.rma_device_check_in.checked_in_at desc, quality.rma_device_check_in.id desc`,
    [rmaId],
  );
  return mapRmaDeviceCheckInList(rmaId, "", devices.rows).devices;
}

export async function listRmaDeviceCheckIns(
  client: PoolClient,
  rmaNumber: string,
): Promise<InboundReceiveRmaDeviceCheckInList> {
  const rma = await client.query<{ id: string; rma_number: string }>(
    `select quality.rma.id::text,
            quality.rma.rma_number
       from quality.rma
      where upper(trim(quality.rma.rma_number)) = $1
        and quality.rma.status::text = any($2::text[])
      limit 1`,
    [normalizeInboundReceiveDeviceSerial(rmaNumber), RMA_DEVICE_CHECK_IN_STATUSES],
  );
  const row = rma.rows[0];
  if (row == null) {
    throw new InboundReceiveRmaDeviceCheckInError("That RMA is not open for receiving.", 404);
  }
  const devices = await loadRmaDeviceCheckInRows(client, row.id);
  return {
    rmaId: row.id,
    rmaNumber: row.rma_number,
    deviceSerials: devices.map((device) => device.deviceSerial),
    devices,
  };
}

export async function recordRmaDeviceCheckIn(
  client: PoolClient,
  input: {
    rmaNumber: string;
    deviceSerial: string;
    packageId?: number | null;
    userId: string;
  },
): Promise<InboundReceiveRmaDeviceCheckInList> {
  const serial = normalizeInboundReceiveDeviceSerial(input.deviceSerial);
  if (serial === "") {
    throw new InboundReceiveRmaDeviceCheckInError("Scan a device label.", 400);
  }
  const current = await listRmaDeviceCheckIns(client, input.rmaNumber);
  const packageId = input.packageId != null && input.packageId > 0 ? input.packageId : null;
  await client.query(
    `insert into quality.rma_device_check_in (
       rma_id,
       rma_device_id,
       device_label_serial,
       inbound_arrival_package_id,
       checked_in_by
     )
     select quality.rma.id,
            (
              select quality.rma_device.id
                from quality.rma_device
               where quality.rma_device.rma_id = quality.rma.id
                 and upper(trim(quality.rma_device.device_label_serial)) = $2
               order by quality.rma_device.id
               limit 1
            ),
            $2,
            $3,
            $4
       from quality.rma
      where quality.rma.id = $1::bigint
     on conflict (rma_id, device_label_serial) do nothing`,
    [current.rmaId, serial, packageId, input.userId],
  );
  await client.query(
    `insert into quality.rma_intake (
       rma_id,
       received_at,
       unit_model,
       unit_serial,
       board_revision
     )
     select quality.rma_device_check_in.rma_id,
            (quality.rma_device_check_in.checked_in_at at time zone 'America/Chicago')::date,
            (
              select public.serial_fci.finished_sku
                from public.serial_fci
               where public.serial_fci.archived_at is null
                 and upper(trim(public.serial_fci.device_label_serial)) = quality.rma_device_check_in.device_label_serial
               order by public.serial_fci.id desc
               limit 1
            ),
            quality.rma_device_check_in.device_label_serial,
            (
              select substring(upper(public.build_order.sku) from '-(R[0-9]+)$')
                from public.serial_fci
                join public.build_order
                  on public.build_order.id = public.serial_fci.pcba_build_order_id
               where public.serial_fci.archived_at is null
                 and upper(trim(public.serial_fci.device_label_serial)) = quality.rma_device_check_in.device_label_serial
                 and (
                   public.build_order.sku ~ '^15-1607-'
                   or public.build_order.sku ~* '^15-1485-W-'
                 )
                 and upper(public.build_order.sku) ~ '-R[0-9]+$'
               order by public.serial_fci.id desc
               limit 1
            )
       from quality.rma_device_check_in
      where quality.rma_device_check_in.rma_id = $1::bigint
        and quality.rma_device_check_in.device_label_serial = $2
        and not exists (
          select 1
            from quality.rma_intake
           where quality.rma_intake.rma_id = quality.rma_device_check_in.rma_id
             and upper(trim(coalesce(quality.rma_intake.unit_serial, ''))) = quality.rma_device_check_in.device_label_serial
        )`,
    [current.rmaId, serial],
  );
  return listRmaDeviceCheckIns(client, current.rmaNumber);
}

export async function deleteRmaDeviceCheckIn(
  client: PoolClient,
  input: { rmaNumber: string; checkInId: number },
): Promise<InboundReceiveRmaDeviceCheckInList> {
  const current = await listRmaDeviceCheckIns(client, input.rmaNumber);
  const checkedIn = await client.query<{ device_label_serial: string }>(
    `select quality.rma_device_check_in.device_label_serial
       from quality.rma_device_check_in
      where quality.rma_device_check_in.id = $1
        and quality.rma_device_check_in.rma_id = $2::bigint`,
    [input.checkInId, current.rmaId],
  );
  const serial = checkedIn.rows[0]?.device_label_serial;
  if (serial == null) {
    throw new InboundReceiveRmaDeviceCheckInError("That checked-in device was not found.", 404);
  }
  await client.query(
    `delete from quality.rma_intake
      where quality.rma_intake.rma_id = $1::bigint
        and upper(trim(coalesce(quality.rma_intake.unit_serial, ''))) = $2
        and quality.rma_intake.visual_inspection is null
        and quality.rma_intake.aa_voltage is null
        and quality.rma_intake.c_voltage is null
        and quality.rma_intake.board_revision is null
        and quality.rma_intake.hybrid_cap is null
        and quality.rma_intake.firmware_version is null
        and quality.rma_intake.resolution is null
        and quality.rma_intake.rca_notes is null`,
    [current.rmaId, serial],
  );
  const removed = await client.query(
    `delete from quality.rma_device_check_in
      where quality.rma_device_check_in.id = $1
        and quality.rma_device_check_in.rma_id = $2::bigint`,
    [input.checkInId, current.rmaId],
  );
  if ((removed.rowCount ?? 0) === 0) {
    throw new InboundReceiveRmaDeviceCheckInError("That checked-in device was not found.", 404);
  }
  return listRmaDeviceCheckIns(client, current.rmaNumber);
}

export class InboundReceiveRmaDeviceCheckInError extends Error {
  readonly httpStatus: number;

  constructor(message: string, httpStatus: number) {
    super(message);
    this.name = "InboundReceiveRmaDeviceCheckInError";
    this.httpStatus = httpStatus;
  }
}

export async function markRmaReceivedAfterDeviceCount(
  client: PoolClient,
  rmaId: number,
): Promise<void> {
  await client.query(
    `update quality.rma
        set status = 'received',
            updated_at = now()
      where quality.rma.id = $1
        and quality.rma.status = 'awaiting_return'`,
    [rmaId],
  );
}
